import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { TopBar } from "@/components/PageShell";
import {
  Gamepad2, Trophy, Sparkles, Play, Lock, CheckCircle2, ArrowRight,
  User, BookOpen, Target, TrendingUp, Zap, Gavel, Building, Landmark,
  Scale, Award, ChevronDown,
} from "lucide-react";

const NAVY = "#0E1D2D";
const GOLD = "#C9953D";

// Asset oficial anexado pelo usuário — pôster épico "O Ilustríssimo".
// Se ainda não estiver renderizando o pôster correto, troque a URL abaixo
// pelo asset certo (ver get_assets_tool).
const ARENA_OAB_IMAGE =
  "https://customer-assets-cm19k8pv.emergentagent.net/job_doc-advocate-1/artifacts/qwverd1h_ChatGPT%20Image%2010%20de%20set.%20de%202026%2C%2016_01_02.png";

// Retrato oficial do "Ilustríssimo" — juiz apresentador da Arena.
// Trocar aqui se o usuário enviar uma nova arte.
const ILUSTRISSIMO_IMAGE =
  "https://customer-assets-cm19k8pv.emergentagent.net/job_doc-advocate-1/artifacts/xtyggx5j_ilustrissimo.png";

// Storyboard épico da Arena OAB — 10 painéis que resumem a jornada.
// Usado como banner do CTA final.
const ARENA_STORYBOARD_IMAGE =
  "https://customer-assets-cm19k8pv.emergentagent.net/job_doc-advocate-1/artifacts/kjtds898_ilustrissimo3.png";

const DIFFICULTY_LADDER = [
  { label: "Extremamente Fácil", desc: "Prove que conhece os fundamentos.", tone: "emerald" },
  { label: "Fácil",              desc: "Vamos aumentar um pouco a pressão.", tone: "lime" },
  { label: "Meio Difícil",       desc: "As alternativas ficam perigosamente parecidas.", tone: "amber" },
  { label: "Difícil",            desc: "Aqui, decorar já não é suficiente.", tone: "orange" },
  { label: "Muito Difícil",      desc: "A Arena separa conhecimento de preparação.", tone: "rose" },
  { label: "Dificílima",         desc: "Chegou até aqui? Prove que merece continuar.", tone: "violet" },
];

const JOURNEY = [
  { name: "Faculdade",             icon: BookOpen,  desc: "Aqui você encontrará seus primeiros desafios." },
  { name: "Biblioteca",            icon: BookOpen,  desc: "Você já conhece os fundamentos. Agora precisará mostrar que estudou." },
  { name: "Escritório",            icon: Building,  desc: "Bem-vindo ao mundo profissional. Conhecimento e interpretação começam a andar juntos." },
  { name: "Fórum",                 icon: Landmark,  desc: "Processos. Prazos. Direitos. O Ilustríssimo já sabe que você está chegando." },
  { name: "Tribunal",              icon: Scale,     desc: "Chegar aqui significa que muita coisa ficou para trás. A margem para erro diminui." },
  { name: "Tribunais Superiores",  icon: Landmark,  desc: "As questões são mais difíceis. As alternativas são mais próximas. Os detalhes importam." },
  { name: "Câmara dos Recursos",   icon: Gavel,     desc: "Pensou que tinha acabado? Advogado deveria saber melhor. Ainda existe recurso." },
  { name: "Tribunal do Ilustríssimo", icon: Award, desc: "As portas se abrem. O salão fica em silêncio. Só você e Ele.", boss: true },
];

const TONE_CLS = {
  emerald: "bg-emerald-50 text-emerald-800 border-emerald-200",
  lime:    "bg-lime-50 text-lime-800 border-lime-200",
  amber:   "bg-amber-50 text-amber-800 border-amber-200",
  orange:  "bg-orange-50 text-orange-800 border-orange-200",
  rose:    "bg-rose-50 text-rose-800 border-rose-200",
  violet:  "bg-violet-50 text-violet-800 border-violet-200",
};

export default function Jogos() {
  const [entitlement, setEntitlement] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let stop = false;
    api.get("/billing/subscriptions/me")
      .then((r) => { if (!stop) setEntitlement(r.data); })
      .catch(() => {})
      .finally(() => !stop && setLoading(false));
    return () => { stop = true; };
  }, []);

  const currentPlan = entitlement?.subscription?.plan_code
    || entitlement?.current_plan?.code
    || "estagiario";
  const arenaIncluded = ["pleno", "senior", "master"].includes(currentPlan);

  return (
    <div className="flex-1 min-w-0 flex flex-col" data-testid="jogos-page">
      <TopBar/>
      <div className="max-w-6xl mx-auto px-6 lg:px-10 py-8">
        <div className="mb-6">
          <div className="text-[10px] uppercase tracking-[0.28em] font-bold" style={{ color: GOLD }}>
            <Gamepad2 className="w-3 h-3 inline mr-1"/> Jogos Hermes
          </div>
          <h1 className="font-heading text-4xl mt-1" style={{ color: NAVY }}>
            Treine. Compita. Evolua.
          </h1>
          <p className="text-sm text-slate-600 mt-2 max-w-2xl">
            Experiências gamificadas para acelerar sua carreira jurídica. Comece pela Arena OAB.
          </p>
        </div>

        {/* HERO ARENA OAB · imagem oficial */}
        <ArenaHero arenaIncluded={arenaIncluded} loading={loading}/>

        {/* NARRATIVA COMPLETA */}
        <ArenaNarrative arenaIncluded={arenaIncluded}/>

        {/* Estrutura escalável — futuros jogos */}
        <div className="mt-12">
          <div className="text-[10.5px] uppercase tracking-widest text-slate-500 mb-3">Em breve</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            <ComingSoonCard title="Duelos de Oratória" desc="Simule sustentações orais com desafios semanais."/>
            <ComingSoonCard title="Ranking do Tribunal" desc="Compare seu desempenho com outros advogados por área."/>
            <ComingSoonCard title="Missões Hermes" desc="Complete objetivos diários e ganhe recompensas."/>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
function ArenaHero({ arenaIncluded, loading }) {
  return (
    <div className="relative rounded-3xl overflow-hidden border border-slate-200 shadow-xl" data-testid="arena-oab-hero">
      <div className="relative aspect-[16/9] bg-slate-900">
        <img src={ARENA_OAB_IMAGE} alt="Arena OAB — O Ilustríssimo"
             loading="eager"
             className="absolute inset-0 w-full h-full object-cover"
             data-testid="arena-oab-image"/>
        {/* Overlay para legibilidade do CTA sobre a imagem */}
        <div className="absolute inset-x-0 bottom-0 p-6 lg:p-8 bg-gradient-to-t from-black/85 via-black/50 to-transparent">
          <div className="max-w-2xl">
            <div className="text-[10.5px] uppercase tracking-[0.32em] font-bold text-amber-300 mb-1">
              <Gamepad2 className="w-3 h-3 inline mr-1"/> Arena OAB
            </div>
            <h2 className="font-heading text-3xl lg:text-4xl font-bold text-white leading-tight">
              Estude. Enfrente. Evolua. Conquiste.
            </h2>
            <p className="text-white/80 text-sm max-w-xl mt-2">
              Questões oficiais da 1ª fase, duelos rápidos, ranking semanal e simulados. Sua jornada até a Carteira da OAB começa agora.
            </p>
            <div className="mt-4 flex items-center gap-3 flex-wrap">
              {loading ? (
                <div className="h-11 w-40 rounded-lg bg-white/10 animate-pulse"/>
              ) : arenaIncluded ? (
                <>
                  <div className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full bg-emerald-500/20 border border-emerald-400/40 text-emerald-200 text-[11.5px] font-semibold"
                       data-testid="arena-included-badge">
                    <CheckCircle2 className="w-3.5 h-3.5"/> Incluído no seu plano
                  </div>
                  <a href="https://arena.hermes.legal" target="_blank" rel="noopener noreferrer"
                    data-testid="arena-access-cta"
                    className="inline-flex items-center gap-2 h-11 px-6 rounded-lg text-slate-900 text-sm font-bold shadow-lg hover:opacity-95"
                    style={{ background: GOLD }}>
                    <Play className="w-4 h-4"/> Entrar na Arena
                  </a>
                </>
              ) : (
                <>
                  <div className="text-white">
                    <div className="text-[11px] uppercase tracking-widest text-white/60">A partir de</div>
                    <div className="text-2xl font-heading font-bold">R$ 29,90 <span className="text-sm text-white/60 font-normal">/mês</span></div>
                  </div>
                  <Link to="/plans" data-testid="arena-subscribe-cta"
                    className="inline-flex items-center gap-2 h-11 px-6 rounded-lg text-slate-900 text-sm font-bold shadow-lg hover:opacity-95"
                    style={{ background: GOLD }}>
                    <Trophy className="w-4 h-4"/> Assinar Arena OAB
                  </Link>
                  <div className="text-[11px] text-white/60">Ou incluído no Pleno e superiores</div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
function ArenaNarrative({ arenaIncluded }) {
  const finalCTA = arenaIncluded ? (
    <a href="https://arena.hermes.legal" target="_blank" rel="noopener noreferrer"
      data-testid="arena-final-cta"
      className="inline-flex items-center gap-2 h-12 px-8 rounded-lg text-slate-900 text-base font-bold shadow-xl hover:opacity-95 transition"
      style={{ background: GOLD }}>
      <Trophy className="w-5 h-5"/> Entrar na Arena
    </a>
  ) : (
    <Link to="/plans" data-testid="arena-final-cta"
      className="inline-flex items-center gap-2 h-12 px-8 rounded-lg text-slate-900 text-base font-bold shadow-xl hover:opacity-95 transition"
      style={{ background: GOLD }}>
      <Trophy className="w-5 h-5"/> Entrar na Arena
    </Link>
  );

  return (
    <div className="mt-10 space-y-10" data-testid="arena-narrative">
      {/* BEM-VINDO */}
      <NarrativeSection tag="BEM-VINDO À ARENA" title="Sua jornada até a Carteira começa agora.">
        <p>Você estudou. Leu códigos. Assistiu aulas. Resolveu questões.</p>
        <p>E provavelmente já descobriu que <strong>conhecer Direito</strong> é muito diferente de enfrentar uma prova com <strong>80 questões</strong>, tempo correndo e uma <strong>Carteira da OAB</strong> em jogo.</p>
        <p>Aqui, a preparação será diferente. Você acaba de entrar no <strong style={{ color: GOLD }}>ARENA OAB</strong>: um ambiente criado para transformar questões, simulados e revisão jurídica em uma verdadeira jornada de evolução.</p>
        <ul className="pl-5 space-y-1 list-disc marker:text-amber-500">
          <li>Seu conhecimento será colocado à prova.</li>
          <li>Suas dificuldades serão identificadas.</li>
          <li>Seu desempenho será acompanhado.</li>
        </ul>
        <p className="font-heading text-lg" style={{ color: NAVY }}>
          E cada resposta fará parte do seu caminho até o objetivo final: <span style={{ color: GOLD }}>A CARTEIRA DA OAB</span>.
        </p>
      </NarrativeSection>

      {/* ESCOLHA SEU PERSONAGEM */}
      <NarrativeSection tag="ESCOLHA SEU PERSONAGEM" title="Antes de entrar, escolha quem irá representá-lo." icon={User}>
        <p>São <strong>8 personagens</strong>, cada um com identidade, personalidade e história próprias.</p>
        <p>Ele estará presente nos seus melhores momentos — e também quando alguma questão conseguir derrubá-lo. Acertou? Ele comemora. Errou? Ele lembra de algo importante:</p>
        <blockquote className="border-l-4 pl-4 italic text-slate-700" style={{ borderColor: GOLD }}>
          "Uma questão errada agora pode significar uma questão certa no dia da prova."
        </blockquote>
      </NarrativeSection>

      {/* MODOS DE JOGO */}
      <div>
        <SectionHeader tag="ESCOLHA COMO ENFRENTAR A ARENA" title="Dois modos, uma jornada."/>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          <ModeCard
            icon={Zap}
            title="Modo Normal"
            subtitle="Quer jogar? Escolha as matérias e entre."
            body="Sequência de questões: quanto mais você acerta, mais a dificuldade aumenta. Ideal pra treinar reflexos e pressão."
            testid="arena-mode-normal"
          />
          <ModeCard
            icon={Target}
            title="Modo Estudo"
            subtitle="Aqui o objetivo é preparação."
            body="Experiência inspirada na 1ª fase do Exame da Ordem — 80 questões. Descubra pontos fracos e aprenda com cada resposta."
            testid="arena-mode-estudo"
          />
        </div>
      </div>

      {/* AVALIAÇÃO DE DESEMPENHO */}
      <NarrativeSection tag="AVALIAÇÃO DE DESEMPENHO" title="A Arena observa sua evolução." icon={TrendingUp}>
        <p>Seus acertos e erros ajudam a mostrar quais matérias você domina e quais ainda precisam de atenção. Não basta saber quantas questões você acertou. É importante entender:</p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-2">
          {[
            "ONDE VOCÊ ESTÁ ERRANDO",
            "POR QUE ESTÁ ERRANDO",
            "O QUE PRECISA MELHORAR",
          ].map((k) => (
            <div key={k} className="rounded-lg border border-amber-200 bg-amber-50/60 p-3 text-center text-[12.5px] font-bold uppercase tracking-wider"
                 style={{ color: NAVY }}>
              {k}
            </div>
          ))}
        </div>
      </NarrativeSection>

      {/* PROGRESSÃO DE DIFICULDADE */}
      <div>
        <SectionHeader tag="A ARENA NÃO VAI FACILITAR" title="A dificuldade evolui com você."/>
        <p className="text-slate-600 text-sm mt-2 max-w-2xl">
          Você começa com desafios acessíveis. Mas não se acostume — a progressão é implacável, e cada nível separa preparação de acaso.
        </p>
        <div className="mt-4 space-y-2">
          {DIFFICULTY_LADDER.map((d, i) => (
            <div key={d.label} data-testid={`arena-diff-${i}`}
              className={`flex items-center gap-3 rounded-lg border p-3 ${TONE_CLS[d.tone]}`}>
              <span className="w-7 h-7 rounded-full bg-white/70 flex items-center justify-center text-[11px] font-bold font-mono">
                {i + 1}
              </span>
              <div className="flex-1 min-w-0">
                <div className="font-heading font-bold uppercase tracking-wider text-[13px]">{d.label}</div>
                <div className="text-[12.5px] opacity-80">{d.desc}</div>
              </div>
              {i < DIFFICULTY_LADDER.length - 1 && (
                <ChevronDown className="w-4 h-4 opacity-40"/>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* O ILUSTRÍSSIMO */}
      <div className="rounded-2xl border border-slate-800 bg-gradient-to-br from-[#0E1D2D] to-[#1a3654] text-white overflow-hidden relative"
           data-testid="arena-boss-block">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 items-stretch">
          {/* Texto */}
          <div className="p-6 lg:p-8 relative order-2 lg:order-1">
            <div className="absolute right-6 top-6 opacity-10 pointer-events-none lg:hidden">
              <Gavel className="w-24 h-24" style={{ color: GOLD }}/>
            </div>
            <div className="relative">
              <div className="text-[10.5px] uppercase tracking-[0.32em] font-bold text-amber-300 mb-1">MAS QUEM COMANDA ESTE LUGAR?</div>
              <h3 className="font-heading text-3xl font-bold leading-tight">O Ilustríssimo</h3>
              <p className="text-white/70 text-sm mt-1">Juiz. Apresentador. Provocador. Senhor absoluto de sua própria Arena.</p>
              <div className="mt-4 space-y-2 text-white/85 text-[13.5px] leading-relaxed">
                <p>Você ouvirá o martelo antes mesmo de vê-lo. Ele não gosta de erros. Não tem paciência. E certamente não está aqui para facilitar sua vida.</p>
                <p>É Ele quem apresenta os desafios, quem aguarda sua resposta, e quem dá o veredito:</p>
              </div>
              <div className="mt-3 flex items-center gap-2 flex-wrap">
                <span className="text-[11px] font-bold uppercase tracking-wider bg-emerald-500/20 border border-emerald-400/40 text-emerald-300 rounded px-2 py-1">CORRETO</span>
                <span className="text-white/40">ou</span>
                <span className="text-[11px] font-bold uppercase tracking-wider bg-rose-500/20 border border-rose-400/40 text-rose-300 rounded px-2 py-1">ERRADO</span>
              </div>
              <p className="text-white/70 text-[13px] mt-4 italic">
                Quando você responder, não haverá negociação. O martelo desce. A resposta é confirmada. Prepare-se — porque Ele estará esperando por você.
              </p>
            </div>
          </div>
          {/* Retrato */}
          <div className="relative order-1 lg:order-2 min-h-[280px] lg:min-h-[420px] bg-gradient-to-br from-amber-900/40 to-[#0E1D2D]">
            <img
              src={ILUSTRISSIMO_IMAGE}
              alt="O Ilustríssimo — juiz apresentador da Arena OAB"
              data-testid="arena-boss-portrait"
              className="w-full h-full object-cover"
              loading="lazy"
            />
            {/* fade lateral desktop, fade inferior mobile */}
            <div className="absolute inset-0 pointer-events-none bg-gradient-to-r from-[#0E1D2D] via-transparent to-transparent hidden lg:block"/>
            <div className="absolute inset-0 pointer-events-none bg-gradient-to-t from-[#0E1D2D]/70 via-transparent to-transparent lg:hidden"/>
            {/* Legenda */}
            <div className="absolute bottom-4 right-4 text-right hidden lg:block">
              <div className="text-[10px] uppercase tracking-[0.32em] font-bold text-amber-300/90 drop-shadow">O guardião da Arena</div>
              <div className="text-white/90 text-[13px] font-semibold drop-shadow">"O martelo desce. Sem negociação."</div>
            </div>
          </div>
        </div>
      </div>

      {/* AQUI, ERRAR FAZ PARTE */}
      <NarrativeSection tag="AQUI, ERRAR FAZ PARTE DO JOGO" title="Uma resposta não termina com 'resposta incorreta'." icon={Sparkles}>
        <p>Você precisa entender o que aconteceu. Por isso, as questões apresentam a explicação da alternativa correta e ajudam você a compreender o erro cometido.</p>
        <p className="font-semibold" style={{ color: NAVY }}>
          O objetivo não é apenas dizer se você acertou. É fazer com que aquele conhecimento permaneça com você.
        </p>
        <p className="text-slate-600 italic">
          O erro que você entende durante o treinamento é um erro que pode deixar de existir na prova.
        </p>
      </NarrativeSection>

      {/* JORNADA */}
      <div>
        <SectionHeader tag="SUA JORNADA COMEÇA NA FACULDADE" title="8 fases até o confronto final."/>
        <p className="text-slate-600 text-sm mt-2 max-w-2xl">
          Ninguém chega aos Tribunais Superiores no primeiro dia. Sua trajetória começa onde muitas histórias jurídicas começam.
        </p>
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          {JOURNEY.map((step, i) => (
            <JourneyStep key={step.name} step={step} index={i}/>
          ))}
        </div>
      </div>

      {/* CARTEIRA */}
      <div className="rounded-2xl border border-amber-200 bg-gradient-to-br from-amber-50 to-white p-6 lg:p-8 text-center">
        <div className="text-[10.5px] uppercase tracking-[0.32em] font-bold" style={{ color: GOLD }}>
          E o que existe no final?
        </div>
        <h3 className="font-heading text-3xl font-bold mt-1 mb-3" style={{ color: NAVY }}>
          A Carteira da OAB.
        </h3>
        <p className="text-slate-700 text-[14px] max-w-xl mx-auto">
          Não são moedas. Não é um tesouro. Não é uma coroa. Para quem chegou até aqui, existe algo muito mais importante.
        </p>
        <p className="text-slate-600 text-[13px] max-w-xl mx-auto mt-3 italic">
          Aqui, ela é uma conquista simbólica. Na vida real, existe apenas uma maneira de conquistá-la: <b style={{ color: NAVY }}>PREPARAÇÃO</b>. E é exatamente para isso que esta Arena existe.
        </p>
      </div>

      {/* QUESTÕES REAIS */}
      <NarrativeSection tag="QUESTÕES REAIS · EVOLUÇÃO REAL" title="Você não está apertando botões. Está treinando.">
        <p>A base do ARENA OAB foi pensada para trabalhar com questões da 1ª fase do Exame da Ordem, organizadas por matéria, dificuldade, resposta e explicação.</p>
        <p>Cada partida pode revelar seus pontos fortes e suas dificuldades. Quanto mais você joga, mais aprende sobre o Direito. E também sobre o seu próprio desempenho.</p>
      </NarrativeSection>

      {/* NÃO TENHA MEDO */}
      <div className="rounded-2xl border border-slate-200 bg-white p-6 lg:p-8">
        <h3 className="font-heading text-2xl font-bold" style={{ color: NAVY }}>
          Não tenha medo de errar.
        </h3>
        <p className="text-slate-700 text-[14px] mt-2">
          Tenha medo de chegar à prova sem descobrir onde estava errando.
        </p>
        <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2">
          {["Tente", "Erre", "Entenda", "Aprenda"].map((w, i) => (
            <div key={w} className="text-center rounded-lg border border-slate-200 bg-slate-50 py-2 font-heading font-bold uppercase tracking-widest text-[12.5px]"
                 style={{ color: NAVY }}>
              {i + 1}. {w}
            </div>
          ))}
        </div>
        <p className="mt-4 text-slate-800 font-semibold" style={{ color: NAVY }}>
          Quantas vezes forem necessárias. Porque quando chegar o dia da prova…
        </p>
        <p className="mt-1 font-heading text-xl uppercase tracking-wider" style={{ color: GOLD }}>
          Não haverá botão de reiniciar.
        </p>
      </div>

      {/* CTA FINAL */}
      <div className="rounded-3xl border border-slate-800 text-center relative overflow-hidden isolate"
           data-testid="arena-final-cta-block">
        {/* Imagem de fundo — storyboard 10 painéis */}
        <img
          src={ARENA_STORYBOARD_IMAGE}
          alt=""
          aria-hidden="true"
          data-testid="arena-storyboard-banner"
          className="absolute inset-0 w-full h-full object-cover -z-10"
          loading="lazy"
        />
        {/* Overlay escuro para legibilidade — mais forte no centro (onde fica texto) */}
        <div className="absolute inset-0 -z-10 bg-gradient-to-br from-[#0E1D2D]/80 via-[#0E1D2D]/55 to-[#0E1D2D]/80"/>
        <div className="absolute inset-0 -z-10 bg-[radial-gradient(ellipse_at_center,rgba(14,29,45,0.75)_0%,rgba(14,29,45,0.35)_60%,rgba(14,29,45,0.6)_100%)]"/>

        <div className="p-8 lg:p-12 relative">
          <div className="absolute inset-0 opacity-10 pointer-events-none">
            <div className="absolute top-4 left-8 w-24 h-24 rounded-full border-2" style={{ borderColor: GOLD }}/>
            <div className="absolute bottom-4 right-12 w-32 h-32 rounded-full border-2" style={{ borderColor: GOLD }}/>
          </div>
          <div className="relative">
            <div className="text-[10.5px] uppercase tracking-[0.32em] font-bold text-amber-300">A Arena está aberta.</div>
            <h3 className="font-heading text-3xl lg:text-5xl font-bold text-white mt-1 drop-shadow-[0_2px_8px_rgba(0,0,0,0.6)]">
              Sua jornada começa agora.
            </h3>
            <p className="text-amber-200 text-sm mt-3 uppercase tracking-widest font-semibold">
              Arena OAB · Estude · Enfrente · Evolua · Conquiste
            </p>
            <div className="mt-6 text-white/95 text-[13px] space-y-0.5 [text-shadow:0_1px_4px_rgba(0,0,0,0.7)]">
              <p>Escolha seu personagem.</p>
              <p>Defina seu modo de jogo.</p>
              <p>Prepare seus conhecimentos.</p>
              <p>Leia cada palavra.</p>
              <p>Desconfie de cada alternativa.</p>
              <p className="text-amber-200 italic mt-2">E quando O Ilustríssimo bater o martelo… esteja preparado para responder.</p>
            </div>
            <div className="mt-6">{finalCTA}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
function NarrativeSection({ tag, title, icon: Icon, children }) {
  return (
    <div>
      <SectionHeader tag={tag} title={title} icon={Icon}/>
      <div className="mt-3 space-y-3 text-slate-700 text-[13.5px] leading-relaxed max-w-3xl">
        {children}
      </div>
    </div>
  );
}

function SectionHeader({ tag, title, icon: Icon }) {
  return (
    <div className="flex items-start gap-3">
      {Icon && (
        <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0 mt-1"
             style={{ background: `${GOLD}18`, border: `1px solid ${GOLD}55`, color: GOLD }}>
          <Icon className="w-4 h-4"/>
        </div>
      )}
      <div>
        <div className="text-[10.5px] uppercase tracking-[0.28em] font-bold" style={{ color: GOLD }}>{tag}</div>
        <h3 className="font-heading text-2xl font-bold" style={{ color: NAVY }}>{title}</h3>
      </div>
    </div>
  );
}

function ModeCard({ icon: Icon, title, subtitle, body, testid }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 hover:shadow-md transition" data-testid={testid}>
      <div className="flex items-center gap-2 mb-2">
        <div className="w-9 h-9 rounded-lg flex items-center justify-center"
             style={{ background: `${GOLD}18`, color: GOLD }}>
          <Icon className="w-4 h-4"/>
        </div>
        <div className="font-heading text-xl font-bold" style={{ color: NAVY }}>{title}</div>
      </div>
      <p className="text-slate-700 font-semibold text-[13px]">{subtitle}</p>
      <p className="text-slate-600 text-[13px] mt-2">{body}</p>
    </div>
  );
}

function JourneyStep({ step, index }) {
  const Icon = step.icon;
  const boss = step.boss;
  return (
    <div data-testid={`arena-journey-${index}`}
      className={`rounded-xl border p-4 transition ${
        boss
          ? "border-amber-400 bg-gradient-to-br from-amber-50 to-white shadow-md"
          : "border-slate-200 bg-white hover:shadow-md"
      }`}>
      <div className="flex items-center gap-2 mb-1">
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${boss ? "text-white" : ""}`}
             style={boss ? { background: GOLD } : { background: `${NAVY}10`, color: NAVY }}>
          <Icon className="w-4 h-4"/>
        </div>
        <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400 font-mono">Fase {index + 1}</span>
      </div>
      <div className="font-heading text-[15px] font-bold" style={{ color: NAVY }}>{step.name}</div>
      <p className="text-[12px] text-slate-600 mt-1 leading-snug">{step.desc}</p>
    </div>
  );
}

function ComingSoonCard({ title, desc }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 opacity-90">
      <div className="flex items-start justify-between mb-2">
        <div className="w-9 h-9 rounded-lg flex items-center justify-center"
             style={{ background: `${GOLD}18`, color: GOLD }}>
          <Sparkles className="w-4 h-4"/>
        </div>
        <span className="text-[9px] font-bold uppercase tracking-widest text-slate-600 bg-slate-100 border border-slate-200 rounded px-1.5 py-0.5">
          <Lock className="w-2.5 h-2.5 inline mr-0.5"/> Em breve
        </span>
      </div>
      <div className="font-heading text-lg font-semibold" style={{ color: NAVY }}>{title}</div>
      <p className="text-[12.5px] text-slate-500 mt-1">{desc}</p>
    </div>
  );
}
